import { MdAdd, MdArchive, MdClose, MdEdit, MdFullscreen, MdFullscreenExit, MdSave, MdUnarchive } from "react-icons/md";
import { TranslationKey } from "../../../../shared/i18n/i18n";
import { AdminActionButton } from "../../common/AdminActionButton";
import { DisabledActionReason } from "../../../utils/adminPermissions";

type AdminDetailActionsProps = {
  archiveDisabledReason?: DisabledActionReason;
  canArchive?: boolean;
  canCreate: boolean;
  createActionLabel?: string;
  canSave?: boolean;
  canUnarchive?: boolean;
  canUpdate?: boolean;
  createDisabledReason?: DisabledActionReason;
  disableNonEditingActions?: boolean;
  hasDetail: boolean;
  isArchived: boolean;
  isBusy: boolean;
  isDesktopNavHidden: boolean;
  isEditing: boolean;
  isMobileViewport: boolean;
  onArchive: () => void;
  onCloseDesktopNav: () => void;
  onCreate: () => void;
  onDiscard: () => void;
  onEdit: () => void;
  onOpenDesktopNav: () => void;
  onSave: () => void;
  onUnarchive: () => void;
  saveDisabledReason?: DisabledActionReason;
  showArchiveActions?: boolean;
  showCreateAction?: boolean;
  showEditAction?: boolean;
  showNavToggle?: boolean;
  unarchiveDisabledReason?: DisabledActionReason;
  updateDisabledReason?: DisabledActionReason;
  t: (key: TranslationKey) => string;
};

export const AdminDetailActions = ({
  archiveDisabledReason = null,
  canArchive = true,
  canSave,
  canUnarchive = canArchive,
  canCreate,
  createActionLabel,
  createDisabledReason = null,
  canUpdate = true,
  disableNonEditingActions = false,
  updateDisabledReason = null,
  hasDetail,
  isArchived,
  isBusy,
  isDesktopNavHidden,
  isEditing,
  isMobileViewport,
  onArchive,
  onCloseDesktopNav,
  onCreate,
  onDiscard,
  onEdit,
  onOpenDesktopNav,
  onSave,
  onUnarchive,
  saveDisabledReason,
  showArchiveActions = true,
  showCreateAction = true,
  showEditAction = true,
  showNavToggle = true,
  unarchiveDisabledReason = archiveDisabledReason,
  t,
}: AdminDetailActionsProps) => {
  const effectiveCanSave = canSave ?? (hasDetail ? canUpdate : canCreate);
  const effectiveSaveDisabledReason = saveDisabledReason ?? (hasDetail ? updateDisabledReason : createDisabledReason);
  const effectiveCreateActionLabel = createActionLabel ?? t("admin.detail.action.create");
  const navToggleAction = showNavToggle && !isMobileViewport ? (
    <button
      aria-label={isDesktopNavHidden ? t("admin.detail.action.showNavigator") : t("admin.detail.action.hideNavigator")}
      className="admin-detail-action"
      disabled={isBusy}
      onClick={isDesktopNavHidden ? onOpenDesktopNav : onCloseDesktopNav}
      title={isDesktopNavHidden ? t("admin.detail.action.showNavigator") : t("admin.detail.action.hideNavigator")}
      type="button"
    >
      {isDesktopNavHidden ? <MdFullscreenExit /> : <MdFullscreen />}
    </button>
  ) : null;

  if (isEditing) {
    return (
      <>
        {navToggleAction}
        <AdminActionButton
          aria-label={t("admin.detail.action.save")}
          className="admin-detail-action"
          disabledReason={effectiveSaveDisabledReason}
          disabled={isBusy || !effectiveCanSave}
          onClick={onSave}
          t={t}
          title={t("admin.detail.action.save")}
        >
          <MdSave />
        </AdminActionButton>
        <button
          aria-label={t("admin.detail.action.discard")}
          className="admin-detail-action"
          disabled={isBusy}
          onClick={onDiscard}
          title={t("admin.detail.action.discard")}
          type="button"
        >
          <MdClose />
        </button>
      </>
    );
  }

  if (!hasDetail && !canCreate) {
    return navToggleAction;
  }

  return (
    <>
      {navToggleAction}
      {showCreateAction ? (
        <AdminActionButton
          aria-label={effectiveCreateActionLabel}
          className="admin-detail-action"
          disabledReason={createDisabledReason}
          disabled={disableNonEditingActions || !canCreate || isBusy}
          onClick={onCreate}
          t={t}
          title={effectiveCreateActionLabel}
        >
          <MdAdd />
        </AdminActionButton>
      ) : null}
      {hasDetail ? (
        <>
          {!isArchived ? (
            <>
              {showEditAction ? (
                <AdminActionButton
                  aria-label={t("admin.detail.action.edit")}
                  className="admin-detail-action"
                  disabledReason={updateDisabledReason}
                  disabled={isBusy || !canUpdate}
                  onClick={onEdit}
                  t={t}
                  title={t("admin.detail.action.edit")}
                >
                  <MdEdit />
                </AdminActionButton>
              ) : null}
              {showArchiveActions ? (
                <AdminActionButton
                  aria-label={t("admin.detail.action.archive")}
                  className="admin-detail-action"
                  disabledReason={archiveDisabledReason}
                  disabled={disableNonEditingActions || isBusy || !canArchive}
                  onClick={onArchive}
                  t={t}
                  title={t("admin.detail.action.archive")}
                >
                  <MdArchive />
                </AdminActionButton>
              ) : null}
            </>
          ) : showArchiveActions ? (
            <AdminActionButton
              aria-label={t("admin.detail.action.unarchive")}
              className="admin-detail-action"
              disabledReason={unarchiveDisabledReason}
              disabled={disableNonEditingActions || isBusy || !canUnarchive}
              onClick={onUnarchive}
              t={t}
              title={t("admin.detail.action.unarchive")}
            >
              <MdUnarchive />
            </AdminActionButton>
          ) : null}
        </>
      ) : null}
    </>
  );
};
